import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Button, Card, PasswordInput, SimpleGrid, Stack, Text, TextInput } from '@mantine/core';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useState } from 'react';
import { motion } from 'framer-motion';
import { notifications } from '@mantine/notifications';
import { authApi } from '../../lib/auth-api';
import { trpc } from '../../lib/trpc';

const Schema = z.object({
  email: z.string().trim().toLowerCase().email(),
  password: z.string().min(8, 'At least 8 characters').max(128),
  vendorTitle: z.string().trim().min(2, 'Business name is required').max(120),
  firstName: z.string().trim().max(80).optional(),
  lastName: z.string().trim().max(80).optional(),
  mobileNumber: z.string().trim().max(40).optional(),
});
type FormValues = z.infer<typeof Schema>;

function Register() {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const [submitting, setSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(Schema) });

  const onSubmit = handleSubmit(async (values) => {
    setSubmitting(true);
    try {
      await authApi.register(values);
      // New registrants are always VendorAdmin by definition; land in the
      // vendor console directly so they don't bounce through the public
      // landing.
      notifications.show({ message: 'Welcome aboard!', color: 'green' });
      // Registration has just established the session, so this is the first
      // point at which we can ask whether the platform runs the guided setup.
      // Fetched rather than redirect-on-arrival so the flag-off journey stays
      // exactly what it is today — a direct landing, with no flash.
      let guided = false;
      try {
        const methods = await utils.integrations.meta.onboardingMethods.fetch();
        guided = methods.onboardingV2Enabled;
      } catch {
        // A brand-new account that can't read the flag still gets an account.
        // The dashboard is the safe destination.
      }
      navigate({ to: guided ? '/vendor/setup' : '/vendor' });
    } catch (err) {
      notifications.show({
        message: (err as Error).message || 'Registration failed',
        color: 'red',
      });
    } finally {
      setSubmitting(false);
    }
  });

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
      <Card>
        <Stack gap="sm">
          <Text fw={600} size="lg">
            Create account
          </Text>
          <form onSubmit={onSubmit}>
            <Stack gap="sm">
              <TextInput
                label="Business name"
                {...register('vendorTitle')}
                error={errors.vendorTitle?.message}
              />
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                <TextInput label="First name" {...register('firstName')} />
                <TextInput label="Last name" {...register('lastName')} />
              </SimpleGrid>
              <TextInput
                label="Email"
                autoComplete="email"
                {...register('email')}
                error={errors.email?.message}
              />
              <TextInput label="Mobile number" {...register('mobileNumber')} />
              <PasswordInput
                label="Password"
                autoComplete="new-password"
                {...register('password')}
                error={errors.password?.message}
              />
              <Button type="submit" loading={submitting}>
                Create account
              </Button>
            </Stack>
          </form>
        </Stack>
      </Card>
    </motion.div>
  );
}

export const Route = createFileRoute('/auth/register')({ component: Register });
